import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../api/api.js';
import { toast } from 'react-toastify';
import {
    FaClipboardCheck, FaSignInAlt, FaSignOutAlt, FaUndo,
    FaSpinner, FaSearch, FaFilter, FaUser, FaBed,
    FaBuilding, FaCheckCircle, FaClock, FaFilePdf, FaPhone
} from 'react-icons/fa';
import DynamicDateInput from '../common/DynamicDateInput.jsx';
import { useGetCheckinDataQuery, useGetEventsQuery } from '../../redux/api/apiSlice';

// ── Helpers ───────────────────────────────────────────────────
const todayISO = (daysOffset = 0) => {
    const now = new Date();
    if (daysOffset) now.setDate(now.getDate() + daysOffset);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', hour12: true
    });
};

const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
    });
};

// ── Booking status badge ───────────────────────────────────────
const BookingStatusBadge = ({ members }) => {
    const checkedIn = members.filter(m => m.checkInTime && !m.checkOutTime).length;
    const checkedOut = members.filter(m => m.checkOutTime).length;
    const total = members.length;
    const fullyIn = checkedIn + checkedOut === total;

    let color = 'bg-gray-100 text-gray-600';
    let label = `0/${total} Checked In`;

    if (checkedIn + checkedOut > 0 && !fullyIn) {
        color = 'bg-amber-100 text-amber-700';
        label = `${checkedIn + checkedOut}/${total} Partial`;
    } else if (fullyIn) {
        color = 'bg-emerald-100 text-emerald-700';
        label = `${total}/${total} Complete`;
    }

    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${color}`}>
            {fullyIn ? <FaCheckCircle /> : <FaClock />}
            {label}
        </span>
    );
};

// ── Person row ────────────────────────────────────────────────
const PersonRow = ({ person, onAction }) => {
    const [loading, setLoading] = useState(null); // 'checkin' | 'checkout' | 'undo'

    const handle = async (action) => {
        setLoading(action);
        try {
            await onAction(person._id, action);
        } finally {
            setLoading(null);
        }
    };

    const isCheckedIn = !!person.checkInTime && !person.checkOutTime;
    const isCheckedOut = !!person.checkOutTime;
    const isPending = !person.checkInTime;

    const genderColor = {
        male: 'bg-blue-50 text-blue-700 border-blue-200',
        female: 'bg-pink-50 text-pink-700 border-pink-200',
        boy: 'bg-sky-50 text-sky-700 border-sky-200',
        girl: 'bg-rose-50 text-rose-700 border-rose-200',
    }[person.gender?.toLowerCase()] || 'bg-gray-50 text-gray-700 border-gray-200';

    const bed = person.bedId;
    const allocationText = bed
        ? <><span className="font-semibold text-primaryDark">{bed.roomId?.buildingId?.name || ''}</span> · Rm <span className="font-medium text-gray-700">{bed.roomId?.roomNumber || '?'}</span> / Bed <span className="font-medium text-gray-700">{bed.name || '?'}</span></>
        : <span className="italic text-gray-400">No bed assigned</span>;

    return (
        <div className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border transition-all duration-200
            ${isCheckedOut ? 'bg-neutral/40 border-gray-200 opacity-75' :
                isCheckedIn ? 'bg-emerald-50/60 border-emerald-200' :
                    'bg-neutral/60 border-gray-200 hover:border-primary/40'}`}>

            {/* Person info */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 text-sm">{person.name}</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border capitalize ${genderColor}`}>
                        {person.gender}, {person.age}y
                    </span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${person.status === 'checkedout' || person.checkOutTime ? 'bg-rose-100 text-rose-700' :
                        person.status === 'checkedin' || person.checkInTime ? 'bg-emerald-100 text-emerald-700' :
                            'bg-amber-100 text-amber-700'
                        } capitalize flex items-center gap-1`}>
                        {person.status || (person.checkOutTime ? 'checked out' : person.checkInTime ? 'checked in' : 'pending')}
                    </span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500 font-medium">
                    <span className="flex items-center gap-1">
                        <FaSignInAlt className="opacity-70" /> In: {person.checkInTime ? formatTime(person.checkInTime) : 'Not yet'}
                    </span>
                    <span className="flex items-center gap-1">
                        <FaSignOutAlt className="opacity-70" /> Out: {person.checkOutTime ? formatTime(person.checkOutTime) : 'Not yet'}
                    </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <FaBed size={9} className="opacity-60" />
                    {allocationText}
                </p>
                <p className="text-xs text-gray-400">
                    Stay: {formatDate(person.stayFrom)} → {formatDate(person.stayTo)}
                </p>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 shrink-0">
                {isPending && (
                    <button
                        onClick={() => handle('checkin')}
                        disabled={!!loading}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 shadow-sm"
                    >
                        {loading === 'checkin' ? <FaSpinner className="animate-spin" size={11} /> : <FaSignInAlt size={11} />}
                        Check In
                    </button>
                )}

                {isCheckedIn && (
                    <>
                        <button
                            onClick={() => handle('checkout')}
                            disabled={!!loading}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60 shadow-sm"
                        >
                            {loading === 'checkout' ? <FaSpinner className="animate-spin" size={11} /> : <FaSignOutAlt size={11} />}
                            Check Out
                        </button>
                        <button
                            onClick={() => handle('undo')}
                            disabled={!!loading}
                            title="Undo check-in"
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-60"
                        >
                            {loading === 'undo' ? <FaSpinner className="animate-spin" size={10} /> : <FaUndo size={10} />}
                        </button>
                    </>
                )}

                {isCheckedOut && (
                    <button
                        onClick={() => handle('undo')}
                        disabled={!!loading}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-60"
                    >
                        {loading === 'undo' ? <FaSpinner className="animate-spin" size={10} /> : <FaUndo size={10} />}
                        Reset
                    </button>
                )}
            </div>
        </div>
    );
};

const BookingCard = ({ booking, onPersonAction }) => {
    const [open, setOpen] = useState(true);
    const { bookingId, bookingNumber, ashramName, contactNumber, city, eventName, stayFrom, stayTo, members } = booking;

    const handleDownloadPdf = async (e) => {
        e.stopPropagation();
        try {
            const response = await api.get(`/bookings/pdf/${bookingId}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Booking-Pass-${bookingNumber || bookingId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Failed to download PDF.');
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
        >
            {/* Header */}
            <button
                className="w-full flex items-center justify-between p-4 text-left hover:bg-background/40 transition-colors"
                onClick={() => setOpen(o => !o)}
            >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0">
                    <div>
                        <span className="font-mono text-xs text-gray-400 block">#{bookingNumber}</span>
                        <span className="font-bold text-gray-800 text-sm">{ashramName || 'N/A'}</span>
                        <span className="text-xs text-gray-500 ml-2">{city}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-500 bg-background px-2 py-0.5 rounded-full">
                            {eventName}
                        </span>
                        <span className="text-xs text-gray-400">
                            {formatDate(stayFrom)} → {formatDate(stayTo)}
                        </span>
                        {contactNumber && (
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                                <FaPhone className="opacity-70" size={10} /> {contactNumber}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-4">
                    <BookingStatusBadge members={members} />
                    <button
                        onClick={handleDownloadPdf}
                        className="text-primary hover:text-primaryDark p-1.5 rounded-full hover:bg-pink-50 transition-colors ml-2 flex items-center shadow-sm border border-transparent hover:border-pink-200"
                        title="Download Booking Pass"
                    >
                        <FaFilePdf size={14} />
                    </button>
                    <span className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>▼</span>
                </div>
            </button>

            {/* Members */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 space-y-2 border-t border-gray-100 pt-3">
                            {members.map(member => (
                                <PersonRow
                                    key={member._id}
                                    person={member}
                                    onAction={onPersonAction}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// ── Main Page ─────────────────────────────────────────────────
const CheckInPage = () => {
    const [startDate, setStartDate] = useState(todayISO());
    const [endDate, setEndDate] = useState(todayISO(3));
    const [eventId, setEventId] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [bookings, setBookings] = useState([]);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const queryParams = useMemo(() => ({
        startDate,
        endDate,
        eventId,
        sortBy,
        sortOrder,
        searchTerm: debouncedSearch
    }), [startDate, endDate, eventId, sortBy, sortOrder, debouncedSearch]);

    const { data: checkinData, isLoading: loading, isError, refetch } = useGetCheckinDataQuery(queryParams);
    const { data: eventsData } = useGetEventsQuery();
    const events = useMemo(() => eventsData || [], [eventsData]);

    useEffect(() => {
        if (checkinData?.bookings) {
            setBookings(checkinData.bookings);
        }
    }, [checkinData]);

    const fetchData = useCallback(() => {
        refetch();
    }, [refetch]);

    useEffect(() => {
        if (isError) {
            toast.error('Failed to load check-in data.');
        }
    }, [isError]);


    const handlePersonAction = async (personId, action) => {
        try {
            const endpoint =
                action === 'checkin' ? `/people/${personId}/checkin` :
                    action === 'checkout' ? `/people/${personId}/checkout` :
                        `/people/${personId}/undo-checkin`;
            const res = await api.post(endpoint);
            const updated = res.data.person;

            // Optimistic update: replace person in local state
            setBookings(prev => prev.map(b => ({
                ...b,
                members: b.members.map(m =>
                    m._id === personId ? { ...m, ...updated } : m
                )
            })));

            const Msg = () => (
                <div className="flex items-center gap-2">
                    {action === 'checkin' ? <FaSignInAlt className="text-emerald-500" /> :
                        action === 'checkout' ? <FaSignOutAlt className="text-rose-500" /> :
                            <FaUndo className="text-gray-500" />}
                    <span>
                        {action === 'checkin' ? `${updated.name} checked in` :
                            action === 'checkout' ? `${updated.name} checked out` :
                                `Reset for ${updated.name}`}
                    </span>
                </div>
            );
            toast.success(<Msg />);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Action failed.');
        }
    };

    // Summary stats
    const totalMembers = bookings.reduce((s, b) => s + b.members.length, 0);
    const totalCheckedIn = bookings.reduce((s, b) => s + b.members.filter(m => m.checkInTime && !m.checkOutTime).length, 0);
    const totalOut = bookings.reduce((s, b) => s + b.members.filter(m => m.checkOutTime).length, 0);
    const totalPending = totalMembers - totalCheckedIn - totalOut;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 md:p-8 bg-neutral min-h-screen font-body">
            {/* Header */}
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-primaryDark border-b-4 border-primary pb-2 inline-block font-heading">
                <FaClipboardCheck className="inline mr-3 text-primary" />
                Check-in / Check-out
            </h2>

            {/* Stats Summary - Integration into the header or top bar if needed */}
            {!loading && totalMembers > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-card p-4 rounded-xl shadow-soft border-l-4 border-blue-500 text-center hover:shadow-md transition-shadow">
                        <div className="text-3xl font-extrabold text-blue-700 font-heading">{totalMembers}</div>
                        <div className="text-xs text-blue-500 font-bold uppercase tracking-wider mt-1">Expected</div>
                    </div>
                    <div className="bg-card p-4 rounded-xl shadow-soft border-l-4 border-amber-500 text-center hover:shadow-md transition-shadow">
                        <div className="text-3xl font-extrabold text-amber-600 font-heading">{totalPending}</div>
                        <div className="text-xs text-amber-500 font-bold uppercase tracking-wider mt-1">Pending</div>
                    </div>
                    <div className="bg-card p-4 rounded-xl shadow-soft border-l-4 border-emerald-500 text-center hover:shadow-md transition-shadow">
                        <div className="text-3xl font-extrabold text-emerald-600 font-heading">{totalCheckedIn}</div>
                        <div className="text-xs text-emerald-500 font-bold uppercase tracking-wider mt-1">Checked In</div>
                    </div>
                    <div className="bg-card p-4 rounded-xl shadow-soft border-l-4 border-rose-500 text-center hover:shadow-md transition-shadow">
                        <div className="text-3xl font-extrabold text-rose-600 font-heading">{totalOut}</div>
                        <div className="text-xs text-rose-500 font-bold uppercase tracking-wider mt-1">Checked Out</div>
                    </div>
                </div>
            )}

            {/* Filters Area - Standard bg-card pattern */}
            <div className="bg-card p-4 rounded-2xl shadow-soft mb-8">
                <h3 className="text-base font-semibold mb-4 flex items-center text-primaryDark font-heading">
                    <FaFilter className="mr-2 text-primary" /> Multi-Filter Search
                </h3>
                <div className="flex flex-col gap-5">
                    {/* Top Row: Dates */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <DynamicDateInput
                                label="From Date"
                                name="startDate"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                            />
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { setStartDate(todayISO()); setEndDate(todayISO()); }}
                                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                                >
                                    Today
                                </button>
                                <button
                                    onClick={() => { setStartDate(todayISO()); setEndDate(todayISO(3)); }}
                                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                                >
                                    3 Days
                                </button>
                                <button
                                    onClick={() => { setStartDate(todayISO()); setEndDate(todayISO(7)); }}
                                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition-colors"
                                >
                                    7 Days
                                </button>
                            </div>
                        </div>
                        <DynamicDateInput
                            label="To Date"
                            name="endDate"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                        />
                        <div className="w-full">
                            <label className="text-sm font-semibold text-gray-700 mb-1 block">Filter by Event</label>
                            <select
                                value={eventId}
                                onChange={e => setEventId(e.target.value)}
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 outline-none h-[42px]"
                            >
                                <option value="">All Events</option>
                                {events.map(ev => (
                                    <option key={ev._id} value={ev._id}>{ev.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Bottom Row: Search & Sort */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                        <div className="md:col-span-6 lg:col-span-8 w-full">
                            <label className="text-sm font-semibold text-gray-700 mb-1 block">Quick Search</label>
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search by Name, City..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white shadow-sm h-[42px]"
                                />
                            </div>
                        </div>

                        <div className="md:col-span-3 lg:col-span-2 w-full">
                            <label className="text-sm font-semibold text-gray-700 mb-1 block">Sort By</label>
                            <select
                                value={sortBy}
                                onChange={e => setSortBy(e.target.value)}
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 outline-none h-[42px]"
                            >
                                <option value="date">Date</option>
                                <option value="name">Name</option>
                            </select>
                        </div>

                        <div className="md:col-span-3 lg:col-span-2 w-full">
                            <label className="text-sm font-semibold text-gray-700 mb-1 block">Order</label>
                            <select
                                value={sortOrder}
                                onChange={e => setSortOrder(e.target.value)}
                                className="w-full p-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-primary/20 outline-none h-[42px]"
                            >
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={fetchData}
                        className="w-full md:w-auto px-10 py-2 bg-primaryDark text-white rounded-lg text-sm font-bold hover:bg-primary transition-all shadow-md h-[42px]"
                    >
                        Apply Filters & Sort
                    </button>
                </div>
            </div>

            {/* Results Grid */}
            {loading ? (
                <div className="flex justify-center items-center py-32">
                    <FaSpinner className="animate-spin text-primaryDark text-4xl" />
                </div>
            ) : bookings.length === 0 ? (
                <div className="text-center py-24 bg-card/50 rounded-3xl border border-dashed border-gray-400 shadow-inner">
                    <FaUser className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-xl font-bold font-heading">No Guests Found</p>
                    <p className="text-gray-400 text-sm mt-1">Found no matches for the selected date or search criteria.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="flex items-center justify-between px-2">
                        <p className="text-sm font-bold text-primaryDark/60 uppercase tracking-widest flex items-center gap-2">
                            <FaBuilding className="text-primary" />
                            Showing {bookings.length} Booking Group{bookings.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    {bookings.map(b => (
                        <BookingCard
                            key={String(b.bookingId)}
                            booking={b}
                            onPersonAction={handlePersonAction}
                        />
                    ))}
                </div>
            )}
        </motion.div>
    );
};

export default CheckInPage;
